import { learnerReturnPath } from 'modules/auth/return-path';
import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { AlertCircle, KeyRound } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  AuthSplitPanel,
  AuthPhoneField,
  AuthPasswordField,
  GoogleSignInButton,
  TelegramBotLogin,
  TelegramMiniAppButton,
} from 'components/auth';
import { isTelegram } from 'lib/telegram';
import { IS_IO } from 'lib/site';
import { usePhoneLogin, type TelegramBotLoginPhase } from 'modules/auth';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { UZ_E164_PATTERN } from 'lib/phone';

const makePhoneLoginSchema = (t: TFunction) =>
  z.object({
    phone: z.string().regex(UZ_E164_PATTERN, t('auth.invalid_phone')),
    password: z.string().min(8, t('auth.password_min')),
  });
type PhoneLoginFormData = z.infer<ReturnType<typeof makePhoneLoginSchema>>;

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const phoneLogin = usePhoneLogin();

  const navState = (location.state as { from?: { pathname: string }; phone?: string }) || {};
  const from = learnerReturnPath(searchParams.get('returnTo')) ?? navState.from?.pathname ?? '/app';

  // Track Telegram login phase so the waiting card can take full width cleanly
  const [telegramPhase, setTelegramPhase] = React.useState<TelegramBotLoginPhase>('idle');
  const isTelegramActive = telegramPhase !== 'idle' && telegramPhase !== 'starting';

  // Inline credential error. The server can't tell us whether the account exists
  // (anti-enumeration), so the message stays generic; the footer carries the
  // "create one" path for users who don't have an account yet.
  const [formError, setFormError] = React.useState<string | null>(null);

  // Surface Google redirect-flow failures. The server-side OAuth callback bounces
  // back here with `?error=google_*` on any failure; show it inline where the
  // user is looking, then strip the param so a refresh doesn't re-surface it.
  // `google_denied` = the user dismissed the consent screen, so we stay silent.
  React.useEffect(() => {
    const error = searchParams.get('error');
    if (!error) return;
    if (error === 'account_not_found') {
      setFormError(t('auth.no_account_signup'));
    } else if (error.startsWith('google') && error !== 'google_denied') {
      setFormError(t('auth.google_failed'));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PhoneLoginFormData>({
    resolver: zodResolver(makePhoneLoginSchema(t)),
    defaultValues: { phone: navState.phone || '', password: '' },
  });

  const onSubmit = async (data: PhoneLoginFormData) => {
    setFormError(null);
    try {
      await phoneLogin.mutateAsync(data);
      // No manual navigate here — setTokens sets isAuthenticated: true and
      // AuthRedirect in router.tsx picks that up and sends the user to /app.
      // Navigating manually before React processes the state update would skip
      // the isFetched reset and prevent useProfile from firing the permissions request.
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } } };
      // 401 = bad credentials: show it inline where the user is looking. Anything
      // else (network, server) falls back to a toast.
      if (err?.response?.status === 401) {
        setFormError(t('auth.invalid_credentials'));
      } else {
        toast.error(t('error'), {
          description: err?.response?.data?.message || t('auth.invalid_credentials'),
        });
      }
    }
  };

  return (
    <AuthSplitPanel showProofPanel={false} title={t('welcome_back')}>
      {/* examing.io: Google only. Phone/SMS and Telegram are Uzbek-market sign-ins. */}
      {IS_IO ? (
        <GoogleSignInButton redirectTo={from} mode="login">Google</GoogleSignInButton>
      ) : (<>
      {/* One-tap providers */}
      {isTelegramActive ? (
        <div className="w-full">
          <TelegramBotLogin redirectTo={from} mode="login" onPhaseChange={setTelegramPhase} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {isTelegram() ? (
            <TelegramMiniAppButton redirectTo={from} mode="login" label="Telegram" />
          ) : (
            <TelegramBotLogin redirectTo={from} mode="login" label="Telegram" onPhaseChange={setTelegramPhase} />
          )}
          <GoogleSignInButton redirectTo={from} mode="login">Google</GoogleSignInButton>
        </div>
      )}

      <div className="my-[22px] flex items-center gap-3 text-[13px] text-muted-foreground">
        <span aria-hidden className="h-px flex-1 bg-border" />
        {t('auth.or_with_password')}
        <span aria-hidden className="h-px flex-1 bg-border" />
      </div>

      {/* Phone + password, open by default */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
        {formError ? (
          <div
            role="alert"
            className="flex items-center gap-2.5 rounded-[10px] border border-error-200 bg-error-50 px-3 py-2.5 text-sm text-error-700 dark:border-error-900/50 dark:bg-error-900/20 dark:text-error-400"
          >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            <span>{formError}</span>
          </div>
        ) : null}

        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState }) => (
            <AuthPhoneField
              label={t('auth.phone_number')}
              value={field.value}
              onChange={(next) => {
                if (formError) setFormError(null);
                field.onChange(next);
              }}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              invalid={Boolean(formError)}
            />
          )}
        />

        <div className="relative">
          <AuthPasswordField
            autoComplete="current-password"
            label={t('password')}
            placeholder={t('enter_your_password')}
            error={errors.password?.message}
            invalid={Boolean(formError)}
            {...register('password', {
              onChange: () => {
                if (formError) setFormError(null);
              },
            })}
          />
          <Link
            to="/forgot-password"
            className="absolute right-0 top-0 text-[13.5px] font-medium text-foreground hover:underline"
          >
            {t('auth.forgot_q')}
          </Link>
        </div>

        <Button type="submit" className="h-[46px] w-full text-[15px] font-semibold" disabled={phoneLogin.isPending}>
          {phoneLogin.isPending ? t('auth.signing_in') : t('sign_in')}
        </Button>
      </form>
      </>)}

      <div className="mt-5 flex flex-col items-center gap-3.5">
        <Button asChild variant="ghost" className="h-10 gap-2 text-sm text-foreground/80 hover:text-foreground">
          <Link to="/join">
            <KeyRound className="h-4 w-4" aria-hidden />
            {t('auth.use_a_code')}
          </Link>
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          {t('auth.new_to_examing')}{' '}
          <Link to="/signup" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2">
            {t('auth.create_account')}
          </Link>
        </p>
      </div>
    </AuthSplitPanel>
  );
};

export default Login;
